import React, { useMemo } from "react";
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Divider,
} from "@mui/material";
import {
  ActionModal,
  NoticeboardButton,
  ACTION_MODAL_FOOTER_PRIMARY_SIZE,
} from "../../shared";
import type { ChildReportCard, ParentChild } from "../../../types/parentFamily";
import { getChildFullName } from "../../../utils/parentFamilyHelpers";
import {
  CAPS_LEVELS,
  REPORT_TERM_KEYS,
  REPORT_TERM_LABELS,
  capsLevelForPercent,
  computeLearnerAverage,
  formatPercent,
  normalizeReportCard,
} from "../../../utils/reportCardCaps";

interface ReportCardViewModalProps {
  open: boolean;
  onClose: () => void;
  report: ChildReportCard | null;
  child: ParentChild | null;
  organizationName?: string;
  onEdit?: () => void;
}

const formatDate = (value?: string) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString("en-ZA", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return value;
  }
};

export const ReportCardViewModal: React.FC<ReportCardViewModalProps> = ({
  open,
  onClose,
  report,
  child,
  organizationName,
  onEdit,
}) => {
  const normalized = useMemo(
    () => (report ? normalizeReportCard(report) : null),
    [report]
  );

  const averages = useMemo(() => {
    if (!normalized) return {};
    const result: Record<string, number | null> = {};
    for (const term of REPORT_TERM_KEYS) {
      result[term] =
        normalized.learnerAverages?.[term] ??
        computeLearnerAverage(normalized.subjects, term);
    }
    return result;
  }, [normalized]);

  if (!normalized || !child) {
    return (
      <ActionModal open={open} onClose={onClose} title="Report card">
        <Typography variant="body2" color="text.secondary">
          Report card unavailable.
        </Typography>
      </ActionModal>
    );
  }

  return (
    <ActionModal
      open={open}
      onClose={onClose}
      title={normalized.periodLabel || "Report card"}
      maxWidth="lg"
      footer={
        <Box
          sx={{
            display: "flex",
            gap: 1,
            justifyContent: "flex-end",
            width: "100%",
          }}
        >
          {onEdit ? (
            <NoticeboardButton
              variant="secondary"
              size={ACTION_MODAL_FOOTER_PRIMARY_SIZE}
              onClick={onEdit}
            >
              Edit
            </NoticeboardButton>
          ) : null}
          <NoticeboardButton
            variant="secondary"
            size={ACTION_MODAL_FOOTER_PRIMARY_SIZE}
            onClick={() => window.print()}
          >
            Print
          </NoticeboardButton>
          <NoticeboardButton
            variant="primary"
            size={ACTION_MODAL_FOOTER_PRIMARY_SIZE}
            onClick={onClose}
          >
            Close
          </NoticeboardButton>
        </Box>
      }
    >
      <Box
        className="report-card-print"
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          "@media print": {
            p: 0,
          },
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {organizationName || "School report"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {[normalized.phase, normalized.title, normalized.academicYear]
              .filter(Boolean)
              .join(" · ")}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 1,
          }}
        >
          <Typography variant="body2">
            <strong>Learner:</strong> {getChildFullName(child)}
          </Typography>
          <Typography variant="body2">
            <strong>Date of birth:</strong>{" "}
            {child.dateOfBirth ? formatDate(child.dateOfBirth) : "—"}
          </Typography>
          <Typography variant="body2">
            <strong>Grade / class:</strong>{" "}
            {[child.grade, child.classSection].filter(Boolean).join(" ") || "—"}
          </Typography>
          <Typography variant="body2">
            <strong>Days absent:</strong>{" "}
            {normalized.daysAbsent != null ? normalized.daysAbsent : "—"}
          </Typography>
        </Box>

        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Subjects</TableCell>
                {REPORT_TERM_KEYS.map((term) => (
                  <TableCell
                    key={term}
                    align="center"
                    colSpan={2}
                    sx={{ fontWeight: 700 }}
                  >
                    {REPORT_TERM_LABELS[term]}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell />
                {REPORT_TERM_KEYS.map((term) => (
                  <React.Fragment key={`${term}-h`}>
                    <TableCell align="center" sx={{ fontSize: "0.75rem" }}>
                      %
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: "0.75rem" }}>
                      Gr avg
                    </TableCell>
                  </React.Fragment>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {normalized.subjects.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={1 + REPORT_TERM_KEYS.length * 2}>
                    <Typography variant="body2" color="text.secondary">
                      No subject marks captured
                      {normalized.overallResult
                        ? ` · Overall: ${normalized.overallResult}`
                        : ""}
                      {normalized.notes ? ` · ${normalized.notes}` : ""}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                normalized.subjects.map((subject) => (
                  <TableRow key={subject.id}>
                    <TableCell>{subject.name}</TableCell>
                    {REPORT_TERM_KEYS.map((term) => {
                      const mark = subject.terms?.[term];
                      const level = capsLevelForPercent(mark?.percent);
                      return (
                        <React.Fragment key={`${subject.id}-${term}`}>
                          <TableCell align="center">
                            {formatPercent(mark?.percent)}
                            {level ? (
                              <Typography
                                component="span"
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block" }}
                              >
                                L{level.level}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell align="center">
                            {formatPercent(mark?.gradeAverage)}
                          </TableCell>
                        </React.Fragment>
                      );
                    })}
                  </TableRow>
                ))
              )}
              <TableRow sx={{ bgcolor: "action.hover" }}>
                <TableCell sx={{ fontWeight: 700 }}>
                  Learner&apos;s average
                </TableCell>
                {REPORT_TERM_KEYS.map((term) => {
                  const avg = averages[term];
                  const level = capsLevelForPercent(avg);
                  return (
                    <React.Fragment key={`avg-${term}`}>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>
                        {avg != null ? `${avg}%` : "—"}
                        {level ? (
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{ display: "block", fontWeight: 500 }}
                          >
                            L{level.level}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell />
                    </React.Fragment>
                  );
                })}
              </TableRow>
            </TableBody>
          </Table>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Class teacher
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
              {normalized.teacherComment || normalized.notes || "—"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Signed {formatDate(normalized.teacherSignedAt)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Principal
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
              {normalized.principalComment || "—"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Signed {formatDate(normalized.principalSignedAt)}
            </Typography>
          </Box>
        </Box>

        {(normalized.schoolClosesOn || normalized.schoolReopensOn) && (
          <Typography variant="body2" color="text.secondary">
            School closes {formatDate(normalized.schoolClosesOn)}
            {normalized.schoolReopensOn
              ? ` · Reopens ${formatDate(normalized.schoolReopensOn)}`
              : ""}
          </Typography>
        )}

        {normalized.overallResult ? (
          <Typography variant="body2">
            <strong>Overall:</strong> {normalized.overallResult}
          </Typography>
        ) : null}

        <Divider />

        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Scale of achievement
          </Typography>
          <Stack spacing={0.35}>
            {CAPS_LEVELS.map((level) => (
              <Typography key={level.level} variant="caption" color="text.secondary">
                Level {level.level}: {level.description} ({level.min}–{level.max}%)
              </Typography>
            ))}
          </Stack>
        </Box>
      </Box>
    </ActionModal>
  );
};
